import CircleIcon from '@mui/icons-material/Circle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import {screenUsageLabel} from 'utils/dPlayerEngine';

function startedAgoLabel(startedAt, now) {
  if (!Number.isFinite(startedAt) || startedAt <= 0) return null;
  const minutes = Math.max(0, Math.floor((now / 1000 - startedAt) / 60));
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
}

/**
 * Player D screen manager: rendered inside PlayerChrome while the session
 * waits for a free screen (PLAYER_STATES.WAITING_SLOT). Pure presentation —
 * data loading, kicking and the auto-start all live in DPlayer.
 */
function ScreenManagerPanel({
  screens = [],
  maxActiveStreams = null,
  activeCount = null,
  ownViewId = null,
  loading = false,
  error = false,
  kickingKeys = [],
  starting = false,
  canKick = true,
  onKick,
  onRetryLoad = null
}) {
  const now = Date.now();
  const rows = Array.isArray(screens) ? screens : [];

  return (
    <Box
      data-testid="screen-manager-panel"
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: 3,
        py: 4,
        textAlign: 'center',
        zIndex: 2,
        overflowY: 'auto',
        backgroundColor: 'rgba(0,0,0,0.82)'
      }}
    >
      <Typography sx={{color: '#fff', fontWeight: 800, fontSize: {xs: 17, sm: 21}}}>
        Sin pantallas libres
      </Typography>
      <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.82)'}}>
        {screenUsageLabel(activeCount, maxActiveStreams)}
      </Typography>

      {starting && (
        <Chip color="success" size="small" label="Pantalla libre. Iniciando…" sx={{fontWeight: 700}} />
      )}

      {loading && rows.length === 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <CircularProgress size={18} sx={{color: '#fff'}} />
          <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.82)'}}>
            Comprobando pantallas activas…
          </Typography>
        </Box>
      )}

      {error && rows.length === 0 && (
        <Alert
          severity="warning"
          variant="filled"
          action={
            onRetryLoad ? (
              <Button color="inherit" size="small" onClick={onRetryLoad}>
                Reintentar
              </Button>
            ) : null
          }
        >
          No hemos podido cargar tus pantallas activas.
        </Alert>
      )}

      {rows.length > 0 && (
        <List
          dense
          sx={{
            width: '100%',
            maxWidth: 460,
            bgcolor: 'rgba(255,255,255,0.06)',
            borderRadius: 2
          }}
        >
          {rows.map((screen) => {
            const isOwn = Boolean(ownViewId) && screen.viewId === ownViewId;
            const kicking = kickingKeys.includes(screen.key);
            const ago = startedAgoLabel(screen.startedAt, now);
            return (
              <ListItem
                key={screen.key}
                secondaryAction={
                  isOwn ? (
                    <Chip size="small" label="Esta pantalla" color="info" />
                  ) : canKick ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={kicking ? <CircularProgress size={14} /> : <StopCircleIcon />}
                      disabled={kicking}
                      onClick={() => onKick(screen)}
                      aria-label={`Cerrar la pantalla que reproduce ${screen.channelName || 'un canal'}`}
                    >
                      {kicking ? 'Cerrando…' : 'Cerrar'}
                    </Button>
                  ) : null
                }
              >
                <CircleIcon sx={{fontSize: 10, mr: 1.25, color: 'success.main'}} />
                <ListItemText
                  primary={screen.channelName || 'Canal en reproducción'}
                  secondary={[screen.variant ? `Player ${screen.variant}` : null, ago]
                    .filter(Boolean)
                    .join(' · ')}
                  primaryTypographyProps={{
                    sx: {color: '#fff', fontWeight: 600},
                    noWrap: true
                  }}
                  secondaryTypographyProps={{sx: {color: 'rgba(255,255,255,0.6)'}}}
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Typography variant="caption" sx={{color: 'rgba(255,255,255,0.6)', maxWidth: 460}}>
        Al cerrar una pantalla, este canal empezará automáticamente.
      </Typography>
    </Box>
  );
}

ScreenManagerPanel.propTypes = {
  screens: PropTypes.arrayOf(
    PropTypes.shape({
      viewId: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      channelName: PropTypes.string,
      variant: PropTypes.string,
      startedAt: PropTypes.number,
      isCurrent: PropTypes.bool
    })
  ),
  maxActiveStreams: PropTypes.number,
  activeCount: PropTypes.number,
  ownViewId: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  kickingKeys: PropTypes.arrayOf(PropTypes.string),
  starting: PropTypes.bool,
  canKick: PropTypes.bool,
  onKick: PropTypes.func.isRequired,
  onRetryLoad: PropTypes.func
};

export default ScreenManagerPanel;
